import '@styles/editField/TimeFrameCard.css';
import addNewIcon from 'images/icons/edit_field/icon_plus.svg';
import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import {
  calculateMinMax,
  validateChangedTimeFrame,
  validateByWeekTimeFrame,
} from 'timeFramesManager';
import TimeFrameEdit from './TimeFrameEdit';
import TimeFrameItem from './TimeFrameItem';
const { getSeasonStartDay, getSeasonEndDay } = require('PlannerUtils');
const { useTranslation } = require('react-i18next');
var { unitDisplayName } = require('unitsManager');
const {
  getDayOptions,
  getWeeklyOptions,
  getIntervalOptions,
} = require('timeFramesManager');
const {
  plannerType,
  weeklyPlannerModes,
} = require('@managers/planningManager');
const { shouldWeekStartWithMonday } = require('@managers/farmingUnitManager');

// Reusable sort function for timeframes by start date
const sortTimeFramesByStartDate = (timeFrames) => {
  return timeFrames.sort((a, b) => {
    const dateA = new Date(a.active_period_start_date || a.start_date);
    const dateB = new Date(b.active_period_start_date || b.start_date);
    return dateB - dateA;
  });
};

// Generate unique IDs for timeframes to avoid React key collisions
let nextTimeFrameId = 0;
const generateTimeFrameId = () => `tf-${nextTimeFrameId++}-${Date.now()}`;

function useTimeFrames(initialTimeFrames) {
  const [timeFrames, setTimeFrames] = useState([]);
  useEffect(() => {
    if (!initialTimeFrames) return;
    const timeFramesArray = Object.values(initialTimeFrames).map((x) => ({
      ...x,
      id: generateTimeFrameId(),
    }));
    // Sort by start date
    const sortedTimeFrames = sortTimeFramesByStartDate(timeFramesArray);
    setTimeFrames(sortedTimeFrames);
  }, [initialTimeFrames]);
  return [timeFrames, setTimeFrames];
}

function getTypeConfig(
  field,
  t,
  selectedFarmingUnit,
  selectedDistributionName,
) {
  const plannerSettings = field.settings.planner_settings;
  switch (plannerSettings.type) {
    case plannerType.DAILY_RECOMMENDATION:
      const startWithMonday = shouldWeekStartWithMonday(
        selectedFarmingUnit,
        selectedDistributionName,
      );
      return {
        key: 'day',
        label: t('day'),
        frequencyOptions: getDayOptions(startWithMonday),
        validationFunction: validateChangedTimeFrame,
        timeFramesKey: 'time_frames',
        showHours: true,
      };
    case plannerType.WEEKLY_RECOMMENDATION:
      const baseConfig = {
        key: 'frequency_x',
        label: t('frequency'),
        showHours: false,
        validationFunction: validateByWeekTimeFrame,
        shouldBlockPicker: true,
      };
      if (plannerSettings.mode === weeklyPlannerModes.WEEKLY) {
        return {
          ...baseConfig,
          frequencyOptions: getWeeklyOptions(),
          timeFramesKey: 'weekly_time_frames',
          defaultOption: 7,
        };
      }
      if (plannerSettings.mode === weeklyPlannerModes.INTERVAL) {
        return {
          ...baseConfig,
          frequencyOptions: getIntervalOptions(),
          timeFramesKey: 'interval_time_frames',
          defaultOption: 1,
        };
      }
      throw new Error(
        `Invalid planner mode: ${plannerSettings.mode}. Expected one of: ${Object.values(weeklyPlannerModes).join(', ')}`,
      );
    default:
      throw new Error(
        `Invalid planner type: ${plannerSettings.type}. Expected one of: ${Object.values(plannerType).join(', ')}`,
      );
  }
}

export const TimeFrameWrapper = ({
  field,
  currentPlannerType,
  onIrrigationTimeFramesChange,
  onAdd,
  onEdit,
  onEditingChange,
  userData,
  ...rest
}) => {
  if (currentPlannerType === plannerType.DISABLED) {
    return null;
  }
  const { t } = useTranslation();

  const [typeConfig, setTypeConfig] = useState(null);
  const [timeFrames, setTimeFrames] = useTimeFrames(
    field.irrigation_system[typeConfig?.timeFramesKey],
  );
  const [editingId, setEditingId] = useState(null);
  const [addingNew, setAddingNew] = useState(false);
  const [openMenuId, setOpenMenuId] = useState(null);

  const selectedDistributionName = useSelector(
    (state) => state.distribution_data.selected_entities.distribution?.name,
  );
  const selectedFarmingUnit = useSelector(
    (state) => state.distribution_data.selected_entities.farming_unit,
  );
  useEffect(() => {
    try {
      setTypeConfig(
        getTypeConfig(field, t, selectedFarmingUnit, selectedDistributionName),
      );
    } catch (err) {
      console.log('err', err);
    }
  }, [
    field.id,
    field.settings.planner_settings.type,
    field.settings.planner_settings.mode,
  ]);

  useEffect(() => {
    const timeFramesArray = Object.values(
      field.irrigation_system[typeConfig?.timeFramesKey] || {},
    ).map((x) => ({ ...x, id: generateTimeFrameId() }));

    // Sort by start date
    const sortedTimeFrames = sortTimeFramesByStartDate(timeFramesArray);

    setTimeFrames(sortedTimeFrames);
    setEditingId(null);
    setAddingNew(false);
    setOpenMenuId(null);
  }, [field.id, typeConfig?.timeFramesKey]);

  // Notify the parent form whenever a timeframe add/edit section opens or
  // closes, so it can block "Update" while an unsaved timeframe edit is open.
  useEffect(() => {
    const isEditing = addingNew || editingId !== null;
    onEditingChange?.(isEditing);
    // Clear the flag if this wrapper unmounts (e.g. planner type switches to
    // DISABLED and remounts via key) so a stale "open edit" can't block Update.
    return () => onEditingChange?.(false);
  }, [addingNew, editingId, onEditingChange]);

  const soilData = useSelector((state) => state.soil_data.soil_data);
  let userUnits =
    userData && 'length' in userData ? unitDisplayName(userData.length) : '';

  // Get SOW and season end dates
  const sowDate = getSeasonStartDay(field);
  const endOfSeason = getSeasonEndDay(field);
  const userLengthUnits = unitDisplayName(userData.length);
  const firstDayOfWeek = field.settings.planner_settings.first_day_of_week;

  const handleCancel = () => {
    setEditingId(null);
    setAddingNew(false);
  };
  const handleAddNew = () => {
    setAddingNew(true);
    setEditingId(null);
  };

  const handleDelete = (id) => {
    const updated = timeFrames.filter((item) => item.id !== id);
    setTimeFrames(updated);
    onIrrigationTimeFramesChange(typeConfig?.timeFramesKey, updated);
  };

  const getTooltipText = (value, units, source, tooltipSource) => {
    if (source.includes('user')) {
      tooltipSource =
        tooltipSource === 'soil'
          ? t('soil_type')
          : t('edit_field_irrigation_timeframe_source_irrigation_rate');
      return (
        t('edit_field_irrigation_timeframe_tooltip') +
        value +
        ' ' +
        units +
        `(${t('source')}: ${tooltipSource})`
      );
    } else {
      return (
        t('edit_field_irrigation_timeframe_tooltip') +
        value +
        ' ' +
        units +
        `(${t('source')}: ${source})`
      );
    }
  };

  // Translate sources as in old code
  const getSourceText = (source) => {
    if (source === 'soil') {
      return t('edit_field_irrigation_timeframe_source_soil');
    } else if (source === 'rate') {
      return t('edit_field_irrigation_timeframe_source_irrigation_rate');
    } else {
      return t('edit_field_irrigation_timeframe_source_user');
    }
  };

  const setSourceContent = (
    irrigationSystem,
    fieldSoilType,
    soilData,
    userUnits,
    timeFrame = {},
    showHours,
  ) => {
    let minTooltipContent = '';
    let maxTooltipContent = '';
    let {
      minPlaceHolder,
      maxPlaceHolder,
      minSource,
      maxSource,
      minTooltipSource,
      maxTooltipSource,
    } = calculateMinMax(
      timeFrame,
      irrigationSystem,
      fieldSoilType,
      soilData,
      userUnits,
      showHours,
    );
    minSource = getSourceText(minSource);
    maxSource = getSourceText(maxSource);
    minTooltipContent = getTooltipText(
      minPlaceHolder,
      userUnits,
      minSource,
      minTooltipSource,
    );
    maxTooltipContent = getTooltipText(
      maxPlaceHolder,
      userUnits,
      maxSource,
      maxTooltipSource,
    );
    return {
      minData: {
        placeholder: minPlaceHolder,
        source: minSource,
        tooltip: minTooltipContent,
      },
      maxData: {
        placeholder: maxPlaceHolder,
        source: maxSource,
        tooltip: maxTooltipContent,
      },
    };
  };

  // Recompute min/max source data for a given (possibly in-progress) timeframe.
  // Passed to TimeFrameEdit so the source updates live as the user edits values.
  const computeSourceData = (timeFrame) =>
    setSourceContent(
      field.irrigation_system,
      field.soil_type,
      soilData,
      userUnits,
      timeFrame,
      typeConfig?.showHours,
    );

  const NoTimeFrames = () => {
    return (
      <div className='no-time-frames'>
        <div className='no-time-frames-text'>{t('add_time_frames')}</div>
      </div>
    );
  };

  let { minData, maxData } = setSourceContent(
    field.irrigation_system,
    field.soil_type,
    soilData,
    userUnits,
    typeConfig?.showHours,
  );

  const HandleTimeFrameChange = (newTimeFrame, validateFrom) => {
    let errors;
    let finalList = [];
    if (validateFrom === 'edit') {
      // Remove the timeframe being edited (matched by its id) before
      // validating, so the edited frame isn't compared against its own
      // unedited copy. NOTE: ids are strings ("tf-..."), so the previous
      // `splice(newTimeFrame.id, 1)` coerced the id to index 0 and always
      // removed the earliest frame instead — making any other frame report a
      // false "same dates" conflict with itself.
      let timeFramesDup = timeFrames.filter((tf) => tf.id !== newTimeFrame.id);
      errors = typeConfig?.validationFunction(
        timeFramesDup,
        newTimeFrame,
        sowDate,
        endOfSeason,
      );
      timeFramesDup.push(newTimeFrame);
      finalList = timeFramesDup;
    } else {
      errors = typeConfig?.validationFunction(
        timeFrames,
        newTimeFrame,
        sowDate,
        endOfSeason,
      );
      finalList = [...timeFrames, newTimeFrame];
    }
    if (errors.length > 0) {
      return errors;
    } else {
      // Sort by start date before setting
      const sortedFinalList = sortTimeFramesByStartDate(finalList);
      setTimeFrames(sortedFinalList);
      setAddingNew(false);
      setEditingId(null);
      onIrrigationTimeFramesChange(typeConfig?.timeFramesKey, sortedFinalList);
    }
  };

  return (
    <div>
      <div className='d-flex flex-column w-100 aling-item-center justify-content-around ml-2 mr-2 pr-4 pt-2 mb-3'>
        <div className='d-flex flex-row justify-content-between'>
          <label className='text-grey45 text-size-16 mb-0 d-block font-weight-bold'>
            {t('irrigation_time_frames')}
          </label>
          <button
            type='button'
            className='btn btn-primary pr-2 pl-2 blue-primary align-items-center d-flex justify-content-center'
            onClick={handleAddNew}
            style={{
              fontSize: 12,
              background: addingNew ? '#D0D3D9' : '#4563C7',
              height: '24px',
              padding: '3px 0px 3px',
            }}
            disabled={addingNew}
          >
            <img
              src={addNewIcon}
              style={{ height: '12px', width: '12px' }}
              className='mx-1'
              alt='add new timeframe'
            />
            {t('add_new_button')}
          </button>
        </div>
        <small
          className='text-grey71 pt-2 pb-0'
          style={{ lineHeight: 1, whiteSpace: 'pre-line' }}
        >
          {`${t('define_time_frames')}\n${t('edit_field_timeframe_no_frequnecy_constraint')}`}
        </small>
      </div>
      {addingNew && (
        <TimeFrameEdit
          key='new-timeframe'
          typeConfig={typeConfig}
          onSave={(data) => HandleTimeFrameChange(data, 'add')}
          id={generateTimeFrameId()}
          onCancel={handleCancel}
          userLengthUnits={userLengthUnits}
          minData={minData}
          maxData={maxData}
          computeSourceData={computeSourceData}
          sowDate={sowDate}
          endOfSeason={endOfSeason}
          firstDayOfWeek={firstDayOfWeek}
          {...rest}
        />
      )}
      {timeFrames.length === 0 && !addingNew && <NoTimeFrames />}
      {timeFrames.map((tf) => {
        let { minData, maxData } = setSourceContent(
          field.irrigation_system,
          field.soil_type,
          soilData,
          userUnits,
          tf,
          typeConfig?.showHours,
        );
        tf.active_period_start_date = tf.active_period_start_date || sowDate;
        if (editingId === tf.id) {
          return (
            <TimeFrameEdit
              key={tf.id}
              tf={tf}
              typeConfig={typeConfig}
              onSave={(data) => HandleTimeFrameChange(data, 'edit')}
              onCancel={handleCancel}
              userLengthUnits={userLengthUnits}
              minData={minData}
              maxData={maxData}
              computeSourceData={computeSourceData}
              sowDate={sowDate}
              endOfSeason={endOfSeason}
              firstDayOfWeek={firstDayOfWeek}
              {...rest}
            />
          );
        } else {
          return (
            <TimeFrameItem
              key={tf.id}
              tf={tf}
              typeConfig={typeConfig}
              onEdit={() => {
                setEditingId(tf.id);
                setOpenMenuId(null);
                setAddingNew(false);
              }}
              onDelete={() => handleDelete(tf.id)}
              userLengthUnits={userLengthUnits}
              showMenu={openMenuId === tf.id}
              onMenuToggle={(id) =>
                setOpenMenuId(openMenuId === id ? null : id)
              }
              minData={minData}
              maxData={maxData}
              {...rest}
            />
          );
        }
      })}
    </div>
  );
};
